import { IonButton } from '@ionic/react';
import { ComponentProps, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import styles from '@components/buttons/SecondaryButton.module.css';
import Icon from '@components/text/Icon';
import SecondaryButtonIcon from '@components/typography/icons/SecondaryButtonIcon';
import SecondaryButtonText from '@components/typography/SecondaryButtonText';
import useKeyboardFocus from '@hooks/useKeyboardFocus';
import { IconName } from '@utils/iconUtils';
import { imageUrl } from '@utils/imageUtils';

type ButtonProps = Pick<
  ComponentProps<typeof IonButton>,
  | 'disabled'
  | 'expand'
  | 'href'
  | 'onClick'
  | 'onKeyUp'
  | 'rel'
  | 'routerLink'
  | 'tabIndex'
  | 'target'
  | 'title'
  | 'type'
>;

type Props = ButtonProps & {
  appleIcon?: boolean;
  googleIcon?: boolean;
  icon?: IconName;
  size?: 'small' | 'medium' | 'large';
  textKey?: string;
};

const SecondaryButton = ({
  appleIcon = false,
  googleIcon = false,
  icon,
  size = 'medium',
  textKey,
  ...buttonProps
}: Props) => {
  const intl = useIntl();
  const keyboardFocus = useKeyboardFocus();

  const buttonBody = useMemo(() => {
    if (appleIcon) {
      return (
        <div className={styles.appleButtonWrapper}>
          <div className={styles.appleButtonIcon}>
            <Icon color="black" icon="apple" />
          </div>
          {textKey && (
            <div className={styles.appleButtonText}>
              <SecondaryButtonText size={size}>
                <FormattedMessage id={textKey} />
              </SecondaryButtonText>
            </div>
          )}
        </div>
      );
    } else if (googleIcon) {
      return (
        <div className={styles.googleButtonWrapper}>
          <div className={styles.googleButtonIcon}>
            <img alt={intl.formatMessage({ id: 'accessibility.altTags.google' })} src={imageUrl('googleLogo')} />
          </div>
          {textKey && (
            <div className={styles.googleButtonText}>
              <SecondaryButtonText size={size}>
                <FormattedMessage id={textKey} />
              </SecondaryButtonText>
            </div>
          )}
        </div>
      );
    } else {
      return (
        <>
          {textKey && (
            <SecondaryButtonText size={size}>
              <FormattedMessage id={textKey} />
            </SecondaryButtonText>
          )}
          {icon && (
            <span className={textKey ? styles.iconWithText : ''}>
              <SecondaryButtonIcon icon={icon} />
            </span>
          )}
        </>
      );
    }
  }, [appleIcon, googleIcon, icon, intl, size, textKey]);

  return (
    <IonButton
      className={`${styles.button}  ${keyboardFocus ? styles.focus : ''} ${googleIcon ? styles.googleButton : ''}  ${
        styles[size]
      }`}
      mode="md"
      {...buttonProps}
    >
      {buttonBody}
    </IonButton>
  );
};

export default SecondaryButton;
